import { History, Users, Grid3x3, Phone, PhoneOff, MessageSquare, User } from "lucide-react";
import { cn } from "../lib/cn";

export type TabId = "history" | "contacts" | "dialpad" | "messages" | "profile";

const LEFT_TABS = [
  { id: "history" as TabId, label: "History", icon: History },
  { id: "contacts" as TabId, label: "Contacts", icon: Users },
];
const RIGHT_TABS = [
  { id: "messages" as TabId, label: "Messages", icon: MessageSquare },
  { id: "profile" as TabId, label: "Profile", icon: User },
];

interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  /** True when the dialpad has a number entered and the SDK is ready to call. */
  canDialCall: boolean;
  /** Called when the centre button is pressed while dialpad is active with a number. */
  onDialCall: () => void;
  /** True while a call is ringing or active — centre button becomes hang-up. */
  isInCall: boolean;
  /** Called when the hang-up centre button is pressed during a call. */
  onHangup: () => void;
  /** Tab IDs that should be rendered disabled (greyed out, unclickable). */
  disabledTabs?: TabId[];
}

function SideTab({
  id: _id,
  label,
  icon: Icon,
  isActive,
  onClick,
  disabled,
}: {
  id: TabId;
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      aria-label={label}
      aria-disabled={disabled}
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-1 pb-1 pt-2 text-[10px] font-medium transition-colors",
        disabled
          ? "cursor-not-allowed opacity-40"
          : isActive
            ? "text-primary"
            : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className={cn("h-5 w-5", isActive ? "stroke-[2.25px]" : "stroke-[1.75px]")} />
      <span>{label}</span>
    </button>
  );
}

export function TabBar({
  activeTab,
  onTabChange,
  canDialCall,
  onDialCall,
  isInCall,
  onHangup,
  disabledTabs,
}: TabBarProps) {
  const isOnDialpad = activeTab === "dialpad";

  // Centre button visual state:
  //  • isInCall → red hang-up button (overrides everything)
  //  • canDialCall (on dialpad + number typed + SDK ready) → full primary (initiate call)
  //  • on dialpad but no number / not ready → primary at 80% opacity
  //  • on any other tab → solid muted (navigate to dialpad)
  const centreClass = isInCall
    ? "bg-destructive text-destructive-foreground shadow-destructive/30 hover:opacity-90"
    : canDialCall
      ? "bg-primary text-primary-foreground shadow-primary/30 hover:opacity-90"
      : isOnDialpad
        ? "bg-primary/80 text-primary-foreground hover:bg-primary/90"
        : "bg-muted text-muted-foreground hover:bg-muted/70";

  const handleCentrePress = () => {
    if (isInCall) {
      onHangup();
    } else if (canDialCall) {
      onDialCall();
    } else {
      onTabChange("dialpad");
    }
  };

  return (
    <nav className="relative z-20 shrink-0" style={{ backgroundColor: "hsl(var(--background))" }}>
      <div className="flex h-[60px] items-stretch">
        {/* Left tabs */}
        {LEFT_TABS.map(({ id, label, icon }) => (
          <SideTab
            key={id}
            id={id}
            label={label}
            icon={icon}
            isActive={activeTab === id}
            onClick={() => onTabChange(id)}
          />
        ))}

        {/* Centre slot — elevated button floats above the bar */}
        <div className="relative flex flex-1 items-center justify-center">
          <button
            onClick={handleCentrePress}
            aria-label={isInCall ? "Hang up" : canDialCall ? "Call" : "Dialpad"}
            className={cn(
              "absolute -top-[22px] flex h-[52px] w-[52px] items-center justify-center rounded-full shadow-md transition-all active:scale-95",
              centreClass,
            )}
          >
            {isInCall ? (
              <PhoneOff className="h-5 w-5" />
            ) : isOnDialpad ? (
              <Phone className="h-5 w-5 fill-current" />
            ) : (
              <Grid3x3 className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Right tabs */}
        {RIGHT_TABS.map(({ id, label, icon }) => (
          <SideTab
            key={id}
            id={id}
            label={label}
            icon={icon}
            isActive={activeTab === id}
            onClick={() => onTabChange(id)}
            disabled={disabledTabs?.includes(id) ?? false}
          />
        ))}
      </div>
    </nav>
  );
}
